import { useRef, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { MdOutlineCelebration, MdOutlineClose } from "react-icons/md";
import axios from "../../config/axiosInstance";
import { EVENTS } from "../../config/apiConfig";
import Modal from "../modals/Modal";
import { todayInput } from "./eventHelpers";

/*
  Criar ou editar um evento.

  O onboarding é o filtro: quem criar a conta por ele, dentro das datas do
  evento, entra no evento sozinha. As datas valem o dia inteiro (do início às
  23h59 do último dia). "Nenhum" deixa o evento só com cupom e vínculo à mão.

  Editar não tira ninguém: quem já está no evento continua, e o que mudar vale
  para quem se cadastrar dali em diante e para quem ainda não tinha evento.

  Os OPERADORES são as pessoas que cobram o plano no balcão. Com pelo menos um
  cadastrado, "Cobrar plano" obriga a escolher quem está cobrando, e isso fica no
  histórico da cliente. Cada operador é um campo editável (renomear mantém o
  mesmo operador e o que ele já cobrou); tirar da lista só o desativa. A linha de
  adicionar fica EM CIMA da lista: embaixo ela descia a cada operador novo e a
  pessoa tinha de rolar o modal para cadastrar o seguinte.
*/

const labelClass =
  "mb-1 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400";
const fieldClass =
  "w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100";

const EventFormModal = ({ event = null, onClose, onSaved }) => {
  const isEdit = Boolean(event?.id);

  const [name, setName] = useState(event?.name || "");
  const [description, setDescription] = useState(event?.description || "");
  const [startsAt, setStartsAt] = useState(event?.startsAt || todayInput());
  const [endsAt, setEndsAt] = useState(event?.endsAt || todayInput());
  const [onboardingVariant, setOnboardingVariant] = useState(
    event ? event.onboardingVariant || "" : "evento"
  );
  const [error, setError] = useState(null);
  // `key` só serve de chave do React (um operador novo ainda não tem id).
  const nextKey = useRef(0);
  const newKey = () => {
    nextKey.current += 1;
    return `novo-${nextKey.current}`;
  };
  const [operators, setOperators] = useState(() =>
    (event?.operators || []).map((operator) => ({
      key: `op-${operator.id}`,
      id: operator.id,
      name: operator.name,
    }))
  );
  const [newOperator, setNewOperator] = useState("");

  const options = useQuery(
    ["events", "options"],
    async () => {
      const { data } = await axios.get(EVENTS.OPTIONS);
      return data.data;
    },
    { staleTime: 60_000 }
  );

  const save = useMutation(
    () => {
      const body = {
        name,
        description,
        startsAt,
        endsAt,
        onboardingVariant: onboardingVariant || null,
        operators: operators.map(({ id, name }) => ({ id, name })),
      };

      return isEdit
        ? axios.put(`${EVENTS.ONE}/${event.id}`, body)
        : axios.post(EVENTS.LIST, body);
    },
    {
      onSuccess: ({ data }) => onSaved?.(data.data),
      onError: (err) =>
        setError(err.response?.data?.message || "Não foi possível salvar o evento."),
    }
  );

  // Mudar o início para depois do fim arrasta o fim junto: o caso comum é um
  // evento de um dia só, e o servidor recusaria o fim antes do início.
  const changeStart = (value) => {
    setStartsAt(value);
    if (value && endsAt && value > endsAt) setEndsAt(value);
  };

  const addOperator = () => {
    const typed = newOperator.trim();
    if (!typed) return;

    const repeated = operators.some(
      (operator) => operator.name.trim().toLowerCase() === typed.toLowerCase()
    );
    if (repeated) {
      setError(`O operador "${typed}" já está na lista.`);
      return;
    }

    setError(null);
    setOperators([...operators, { key: newKey(), id: null, name: typed }]);
    setNewOperator("");
  };


  const canSave = name.trim() && startsAt && endsAt && !save.isLoading;

  return (
    <Modal
      isOpen
      onClose={() => (save.isLoading ? null : onClose())}
      title={isEdit ? "Editar evento" : "Novo evento"}
      subtitle={isEdit ? event.name : "Quem chegar por ele fica registrada nos números"}
      icon={MdOutlineCelebration}
      size="md"
      closeOnBackdrop={false}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={save.isLoading}
            className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            disabled={!canSave}
            className="rounded-xl bg-pink-600 px-5 py-2 text-sm font-bold text-white hover:bg-pink-700 disabled:opacity-50"
          >
            {save.isLoading ? "Salvando..." : isEdit ? "Salvar" : "Criar evento"}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <label htmlFor="event-name" className={labelClass}>
            Nome do evento
          </label>
          <input
            id="event-name"
            value={name}
            maxLength={120}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex.: Feira Beleza SP 2026"
            className={fieldClass}
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="event-starts" className={labelClass}>
              Começa em
            </label>
            <input
              id="event-starts"
              type="date"
              value={startsAt}
              onChange={(e) => changeStart(e.target.value)}
              className={fieldClass}
            />
          </div>
          <div>
            <label htmlFor="event-ends" className={labelClass}>
              Termina em
            </label>
            <input
              id="event-ends"
              type="date"
              value={endsAt}
              min={startsAt}
              onChange={(e) => setEndsAt(e.target.value)}
              className={fieldClass}
            />
          </div>
        </div>

        <div>
          <label htmlFor="event-onboarding" className={labelClass}>
            Onboarding do evento
          </label>
          <select
            id="event-onboarding"
            value={onboardingVariant}
            onChange={(e) => setOnboardingVariant(e.target.value)}
            className={fieldClass}
          >
            <option value="">Nenhum (só cupom e vínculo à mão)</option>
            {(options.data?.onboardings || []).map((option) => (
              <option key={option.variant} value={option.variant}>
                {option.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-[11px] text-slate-500">
            {onboardingVariant
              ? "Quem criar a conta por esse cadastro, dentro das datas do evento, entra sozinha no evento."
              : "Sem onboarding, as clientes entram pelo cupom do evento ou quando a equipe vincula."}
            {isEdit ? " Quem já está no evento continua nele." : ""}
          </p>
        </div>

        <fieldset>
          <legend className={labelClass}>Operadores (quem cobra o plano)</legend>

          <div className="flex items-center gap-2">
            <input
              id="event-new-operator"
              value={newOperator}
              maxLength={120}
              placeholder="Nome de quem vai cobrar"
              onChange={(e) => setNewOperator(e.target.value)}
              onKeyDown={(e) => {
                // Enter adiciona o operador em vez de enviar o formulário.
                if (e.key === "Enter") {
                  e.preventDefault();
                  addOperator();
                }
              }}
              className={fieldClass}
            />
            <button
              type="button"
              onClick={addOperator}
              disabled={!newOperator.trim()}
              className="shrink-0 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Adicionar
            </button>
          </div>
          <p className="mt-1 text-[11px] text-slate-500">
            {operators.length
              ? "Na hora de cobrar, a equipe escolhe quem está cobrando e isso fica no histórico da cliente."
              : "Sem operadores, a cobrança não pergunta quem cobrou. Cadastre para registrar quem passou o cartão."}
          </p>

          {operators.length ? (
            <ul className="mt-3 space-y-2">
              {operators.map((operator) => (
                <li key={operator.key} className="flex items-center gap-2">
                  <input
                    value={operator.name}
                    maxLength={120}
                    aria-label="Nome do operador"
                    onChange={(e) =>
                      setOperators(
                        operators.map((item) =>
                          item.key === operator.key
                            ? { ...item, name: e.target.value }
                            : item
                        )
                      )
                    }
                    className={fieldClass}
                  />
                  <button
                    type="button"
                    aria-label={`Tirar ${operator.name || "operador"}`}
                    title="Tirar da lista"
                    onClick={() =>
                      setOperators(operators.filter((item) => item.key !== operator.key))
                    }
                    className="shrink-0 rounded-lg p-2 text-slate-400 transition hover:bg-rose-50 hover:text-rose-600"
                  >
                    <MdOutlineClose size={18} />
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </fieldset>


        <div>
          <label htmlFor="event-description" className={labelClass}>
            Observações (opcional)
          </label>
          <textarea
            id="event-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder="Local, estande, quem da equipe vai..."
            className={fieldClass}
          />
        </div>

        {error ? (
          <p
            role="alert"
            className="rounded-xl bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-800 ring-1 ring-inset ring-rose-200"
          >
            {error}
          </p>
        ) : null}
      </div>
    </Modal>
  );
};

export default EventFormModal;
